import { AfterViewInit, Component, Inject, Input, OnInit, Optional, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { Observable, of } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { resolveAudienceDisplayName } from '../../utils/audience-display.util';
import { AudienceResource } from '../../models/audience-resource.model';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { IChoosenAudiencesInterface } from 'app/library/all-users/interfaces/choosen-audiences.interface';
import { AudienceFilterComponent } from 'app/library/all-users/components/filter/audience-filter/audience-filter.component';

export type GrowMoveAudiencePickMode = 'moveMembers' | 'defaultAudiences';

/** Pass via `MatDialog.open(..., { data })` so `pickMode` is set before `app-audience-filter` initializes */
export interface GrowMoveAudienceMembersDialogData {
    pickMode?: GrowMoveAudiencePickMode;
    defaultSettingExperienceId?: string | null;
    /** Pre-existing default audiences from the host page; rendered as chips and pre-selected in the tree */
    preselectedDefaultAudiences?: MaxBrainAudience[];
}

@Component({
    selector: 'app-grow-move-audience-members-dialog',
    templateUrl: './grow-move-audience-members-dialog.component.html',
    styleUrls: ['./grow-move-audience-members-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class GrowMoveAudienceMembersDialogComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @Input()
    pickMode: GrowMoveAudiencePickMode = 'moveMembers';

    /** Required when `pickMode` is `defaultAudiences` (omit experience filter when null) */
    @Input()
    defaultSettingExperienceId: string | null = null;

    @Input()
    audienceListTree: AudienceResource;

    @Input()
    entityId: string;

    @Input()
    selectedUserId$: Observable<string[]> = of([]);
    selectedUserIds: [{ id: string }];

    @Input()
    title: string;

    @Input()
    label: string;

    @ViewChild(AudienceFilterComponent) audienceList: AudienceFilterComponent;
    selectedAudiences: MaxBrainAudience[];

    isUnassignedAudienceSelected: boolean;
    parent = null;

    selectedAudience: MaxBrainAudience;

    /** `defaultAudiences` pick mode: kept in sync from filter events (not read via method in the template). */
    hasDefaultAudiencePickSelection = false;

    /**
     * Union of pre-existing defaults (from the host `#audiencesTable`) and items added in this dialog session.
     * Used both for rendering the chip-grid and as the @Input for the audience tree to mark nodes as selected.
     */
    alreadySelectedAudiences: IChoosenFilterInterface[] = [];
    removable = true;

    private _preselectedDefaultAudiences: MaxBrainAudience[] = [];
    private _initialPreExistingIds = new Set<string>();

    constructor(
        public dialogRef: MatDialogRef<GrowMoveAudienceMembersDialogComponent>,
        private _allUsersService: AllUsersApiService,
        private _translateService: TranslateService,
        @Optional() @Inject(MAT_DIALOG_DATA) injectedData: GrowMoveAudienceMembersDialogData | null
    ) {
        super();
        if (injectedData?.pickMode != null) {
            this.pickMode = injectedData.pickMode;
        }
        if (injectedData && 'defaultSettingExperienceId' in injectedData) {
            this.defaultSettingExperienceId = injectedData.defaultSettingExperienceId ?? null;
        }
        if (injectedData?.preselectedDefaultAudiences?.length) {
            this._preselectedDefaultAudiences = injectedData.preselectedDefaultAudiences;
        }
    }

    ngOnInit() {
        this.selectedUserId$
            .pipe(
                filter((selectedUserIds) => !!selectedUserIds),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((selectedUserIds) => {
                this.selectedUserIds = selectedUserIds.map((id) => ({ id: id })) as [{ id: string }];
            });

        this._allUsersService.selectedAudience$.pipe(takeUntil(this._unsubscribeAll)).subscribe((audience) => {
            this.selectedAudience = audience;
        });

        if (this.pickMode === 'defaultAudiences') {
            this.alreadySelectedAudiences = this._preselectedDefaultAudiences.map((audience) => this._toChoosenFilter(audience));
            this._initialPreExistingIds = new Set(this._preselectedDefaultAudiences.map((audience) => audience.id));
            this.hasDefaultAudiencePickSelection = this.alreadySelectedAudiences.length > 0;
        }
    }

    ngAfterViewInit(): void {
        if (this.pickMode === 'defaultAudiences') {
            queueMicrotask(() => this._syncDefaultAudiencePickSelection());
        }
    }

    private _toChoosenFilter(audience: MaxBrainAudience): IChoosenFilterInterface {
        return {
            entity: { id: audience.id, type: 'Audience' },
            valueToShow: `${this._translateService.instant('ENGAGE.AUDIENCE.TITLE.SINGULAR')}: ${resolveAudienceDisplayName(audience.name, (key) =>
                this._translateService.instant(key)
            )}`,
        };
    }

    private _syncDefaultAudiencePickSelection(): void {
        if (this.pickMode !== 'defaultAudiences') {
            return;
        }
        this.hasDefaultAudiencePickSelection = this.alreadySelectedAudiences.length > 0 || this._initialPreExistingIds.size > 0;
    }

    onConfirm(): void {
        if (this.pickMode === 'defaultAudiences') {
            const currentIds = new Set(this.alreadySelectedAudiences.map((item) => item.entity.id));
            const audienceIds: string[] = [];
            currentIds.forEach((id) => {
                if (!this._initialPreExistingIds.has(id)) {
                    audienceIds.push(id);
                }
            });
            const removedAudienceIds: string[] = [];
            this._initialPreExistingIds.forEach((id) => {
                if (!currentIds.has(id)) {
                    removedAudienceIds.push(id);
                }
            });
            this.dialogRef.close({ audienceIds, removedAudienceIds });
            return;
        }
        this.dialogRef.close({ source: this.entityId, target: this.selectedAudience.id, selectedUserIds: this.selectedUserIds });
    }

    addToList(audience: IChoosenFilterInterface[] | MaxBrainAudience): void {
        if (this.pickMode === 'defaultAudiences') {
            return;
        }
        if (audience && !Array.isArray(audience)) {
            this._allUsersService.selectAudience(audience as MaxBrainAudience, true);
        }
    }

    loadAudienceList(payload: IChoosenAudiencesInterface): void {
        this.selectedAudiences = payload.audiences as MaxBrainAudience[];
        this.isUnassignedAudienceSelected = payload.isUnassignedSelected;
    }

    onAudienceFilterListChange(audience: IChoosenFilterInterface[] | MaxBrainAudience): void {
        this.addToList(audience);

        if (this.pickMode === 'defaultAudiences') {
            // The audience-filter mutates its own `alreadySelectedAudiences` ref when items are deselected in the tree,
            // so combine it with its session `list` to derive the current chip state.
            const stillSelectedPreExisting = this.audienceList?.alreadySelectedAudiences ?? [];
            const sessionAdditions = this.audienceList?.list ?? [];
            const combined: IChoosenFilterInterface[] = [...stillSelectedPreExisting];
            for (const item of sessionAdditions) {
                if (!combined.some((existing) => existing.entity.id === item.entity.id)) {
                    combined.push(item);
                }
            }
            this.alreadySelectedAudiences = combined;
        }

        this._syncDefaultAudiencePickSelection();
    }

    /** Chip-grid X handler — removes the audience from both the chip list and the tree selection state. */
    removeFromList(item: IChoosenFilterInterface, index: number): void {
        if (!item || index < 0) {
            return;
        }
        this.alreadySelectedAudiences = this.alreadySelectedAudiences.filter((existing) => existing.entity.id !== item.entity.id);
        this.audienceList?.removeByEntityId(item.entity.id);
        this._syncDefaultAudiencePickSelection();
    }
}
